import React, { useState } from "react";
import { Link } from "react-router-dom";
import { GraduationCap, Loader2, AlertCircle, ArrowLeft, MailCheck, Send } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [message, setMessage] = useState("");
  // Popolato solo dal backend in dev (DEV_HELPERS_ENABLED): consente di
  // completare il flusso quando l'SMTP non è ancora configurato.
  const [debugUrl, setDebugUrl] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email) {
      setError("Inserisci l’indirizzo email del tuo account.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setMessage(data.message);
      setDebugUrl(data.debug_reset_url || "");
      setSent(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="forgot-password-page"
      className="min-h-screen bg-brand-bg brand-grid-bg flex flex-col"
    >
      <div className="px-4 sm:px-6 lg:px-8 py-6">
        <Link
          to="/login"
          data-testid="forgot-back-login"
          className="inline-flex items-center gap-1.5 text-sm text-brand-blue hover:underline underline-offset-4"
        >
          <ArrowLeft size={16} /> Torna all’accesso
        </Link>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 pb-16">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-2.5 justify-center mb-8">
            <span className="inline-flex h-11 w-11 items-center justify-center rounded-sm bg-brand-blue text-white">
              <GraduationCap size={22} strokeWidth={2} />
            </span>
            <div className="leading-tight">
              <div className="wordmark text-brand-blue text-xl">Buro Academy</div>
              <div className="text-xs text-brand-muted -mt-0.5">Burocratica Servizi</div>
            </div>
          </div>

          <div className="bg-white border border-brand-line rounded-sm shadow-sm p-8">
            <div className="gold-rule" />

            {sent ? (
              <div data-testid="forgot-sent">
                <div className="flex items-center gap-2 text-brand-blue mb-3">
                  <MailCheck size={22} />
                  <h1 className="text-2xl text-brand-ink">Controlla la tua email</h1>
                </div>
                <p className="text-sm text-brand-muted mb-4">{message}</p>
                <p className="text-sm text-brand-muted mb-6">
                  Il link è valido per un tempo limitato e può essere usato una sola
                  volta. Se non trovi il messaggio, controlla anche la cartella
                  spam o posta indesiderata.
                </p>

                {debugUrl && (
                  <div className="mb-6 rounded-sm border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
                    <div className="font-semibold mb-1">Modalità sviluppo</div>
                    <a
                      href={debugUrl}
                      data-testid="forgot-debug-link"
                      className="text-brand-blue underline break-all"
                    >
                      {debugUrl}
                    </a>
                  </div>
                )}

                <Link
                  to="/login"
                  data-testid="forgot-goto-login"
                  className="btn-primary w-full inline-flex items-center justify-center gap-2"
                >
                  Torna all’accesso
                </Link>
              </div>
            ) : (
              <>
                <h1 className="text-2xl text-brand-ink mb-1">Password dimenticata</h1>
                <p className="text-sm text-brand-muted mb-6">
                  Inserisci l’email del tuo account: ti invieremo un link per
                  impostare una nuova password. La procedura è disponibile per
                  studenti, formatori e amministratori.
                </p>

                <form onSubmit={handleSubmit} className="space-y-5" data-testid="forgot-form">
                  <div>
                    <Label htmlFor="email" className="text-sm text-brand-ink">
                      Email
                    </Label>
                    <Input
                      id="email"
                      data-testid="forgot-email-input"
                      type="email"
                      autoComplete="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="nome@burocratica.it"
                      className="mt-1.5 h-11 rounded-sm border-brand-line focus-visible:ring-2 focus-visible:ring-brand-blue"
                      required
                    />
                  </div>

                  {error && (
                    <div
                      data-testid="forgot-error"
                      role="alert"
                      className="flex items-start gap-2 rounded-sm border border-red-200 bg-red-50 p-3 text-sm text-red-800"
                    >
                      <AlertCircle size={16} className="mt-0.5 shrink-0" />
                      <span>{error}</span>
                    </div>
                  )}

                  <button
                    type="submit"
                    data-testid="forgot-submit-btn"
                    disabled={loading}
                    className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <>
                        <Loader2 size={18} className="animate-spin" /> Invio in corso…
                      </>
                    ) : (
                      <>
                        <Send size={16} /> Invia il link di reimpostazione
                      </>
                    )}
                  </button>
                </form>
              </>
            )}
          </div>

          <p className="text-xs text-center text-brand-muted mt-6">
            Problemi di accesso? Contatta la tua amministrazione o Burocratica Servizi.
          </p>
        </div>
      </div>
    </div>
  );
}
